import { resolveAuthContextForEndpoint } from "@Front/Api/helpers/authTokenResolutionHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { TokenRefreshService, type TokenRefreshResult } from "@Front/Services/TokenRefreshService/TokenRefreshService";
import {
	getLeCoffreRefreshTokenCookieForContext,
	getLeCoffreRefreshTokenCookieForEndpoint,
	type LeCoffreAuthContext,
} from "@Front/Utils/lecoffreSessionCookieHelpers";
import { isNetworkError } from "@Front/Utils/errorClassifiers";

export type SessionRefreshRecoveryOutcome = "recovered" | "auth_failed" | "transient";

async function refreshTokenForAuthContext(
	context: LeCoffreAuthContext,
	refreshToken: string,
): Promise<TokenRefreshResult> {
	if (context === "customer") {
		return TokenRefreshService.refreshCustomerToken(refreshToken);
	}
	if (context === "thirdparty") {
		return TokenRefreshService.refreshThirdPartyToken(refreshToken);
	}
	return TokenRefreshService.refreshUserToken(refreshToken);
}

function classifyRefreshFailure(result: TokenRefreshResult): SessionRefreshRecoveryOutcome {
	if (result.failureKind === "network" || result.failureKind === "transient") {
		return "transient";
	}
	if (result.httpStatus === 401 || result.httpStatus === 403) {
		return "auth_failed";
	}
	if (result.httpStatus !== undefined && result.httpStatus >= 500) {
		return "transient";
	}
	if (result.failureKind === "persist" || result.failureKind === "payload") {
		return "auth_failed";
	}
	return "transient";
}

async function runRefreshRecoveryAttempt(
	context: LeCoffreAuthContext,
	refreshToken: string,
	endpoint: string | undefined,
	logPrefix: string,
): Promise<SessionRefreshRecoveryOutcome> {
	const result = await refreshTokenForAuthContext(context, refreshToken);
	if (result.success) {
		LoggerService.info(`✅ [${logPrefix}] Session recovered via refresh after auth failure`, {
			endpoint: endpoint ?? null,
			context,
		});
		return "recovered";
	}
	const outcome = classifyRefreshFailure(result);
	LoggerService.warn(`⚠️ [${logPrefix}] Refresh after auth failure did not recover session`, {
		endpoint: endpoint ?? null,
		context,
		outcome,
		httpStatus: result.httpStatus ?? null,
		failureKind: result.failureKind ?? null,
	});
	return outcome;
}

/**
 * After a 401, attempt a single refresh using the endpoint family's refresh cookie
 * before tearing down the SPA session (avoids wiping a newer session after stale JWT).
 * Distinguishes confirmed auth failure from transient/network refresh failure.
 */
export async function tryRecoverSessionViaRefresh(
	endpoint: string | undefined,
	logPrefix: string,
): Promise<SessionRefreshRecoveryOutcome> {
	const context = resolveAuthContextForEndpoint(endpoint);
	const refreshToken =
		getLeCoffreRefreshTokenCookieForEndpoint(endpoint) ?? getLeCoffreRefreshTokenCookieForContext(context);
	if (!refreshToken) {
		return "auth_failed";
	}
	try {
		return await runRefreshRecoveryAttempt(context, refreshToken, endpoint, logPrefix);
	} catch (error) {
		LoggerService.warn(`⚠️ [${logPrefix}] Refresh recovery threw — not tearing down session`, {
			endpoint: endpoint ?? null,
			error,
			isNetworkError: isNetworkError(error),
		});
		return "transient";
	}
}
